// EnergyClubDashboard.js
import React, { useState, useEffect } from "react";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsivePie } from "@nivo/pie";
import axios from "axios";
import { API_BASE } from "../config";

export default function EnergyClubDashboard() {
  const [energyData, setEnergyData] = useState([]);
  const [pieData, setPieData] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [activities, setActivities] = useState([]);

  const [selectedDistrict, setSelectedDistrict] = useState("0");
  const [selectedActivity, setSelectedActivity] = useState("0");

  const getCurrentFY = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    return month < 4 ? `${year - 1}-${year}` : `${year}-${year + 1}`;
  };

  const currentFY = getCurrentFY();
  const [selectedFY, setSelectedFY] = useState(currentFY);

  const getFYList = () => {
    const [start, end] = currentFY.split("-").map(Number);
    return [`${start - 1}-${end - 1}`, `${start}-${end}`, `${start + 1}-${end + 1}`];
  };
  const financialYears = getFYList();

  // applied filters for heading / API
  const [appliedDistrict, setAppliedDistrict] = useState("0");
  const [appliedActivity, setAppliedActivity] = useState("0");
  const [appliedFY, setAppliedFY] = useState(currentFY);

  const getFYDates = (fy) => {
    const [startYear, endYear] = fy.split("-");
    return { fy_start: `${startYear}-04-01`, fy_end: `${endYear}-03-31` };
  };

  useEffect(() => {
    axios.get(`${API_BASE}/districts`).then((r) => setDistricts(r.data)).catch(() => {});
    axios.get(`${API_BASE}/activities`).then((r) => setActivities(r.data)).catch(() => {});
  }, []);

  const fetchEnergyClub = async (dist = appliedDistrict, activity = appliedActivity, fy = appliedFY) => {
    try {
      const { fy_start, fy_end } = getFYDates(fy);
      const res = await axios.get(`${API_BASE}/custom-submission-summary`, {
        params: { dist, activity, fy_start, fy_end },
      });

      const barChartData = res.data.map((row) => ({
        name: row.district_name,
        district_id: String(row.dist_id),
        count: Number(row.total_submissions || 0),
      }));

      const pieChartData = res.data.map((row) => ({
        id: String(row.dist_id),
        label: row.district_name,
        value: Number(row.total_submissions || 0),
        district_id: String(row.dist_id),
      }));

      setEnergyData(barChartData);
      setPieData(pieChartData);
    } catch (err) {
      console.error("Error fetching Energy Club data:", err);
      setEnergyData([]);
      setPieData([]);
    }
  };

  useEffect(() => {
    fetchEnergyClub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const appliedDistrictName =
    appliedDistrict === "0" ? "All" : districts.find((d) => String(d.dist_id) === appliedDistrict)?.district || "All";
  const appliedActivityName =
    appliedActivity === "0" ? "All" : activities.find((a) => String(a.activities_id) === appliedActivity)?.activity || "All";
  const appliedFYLabel = appliedFY || "All";

  const barChartSettings = {
    keys: ["count"],
    indexBy: "name",
    margin: { top: 80, right: 50, bottom: 160, left: 70 },
    padding: 0.35,
    borderRadius: 6,
    colors: (bar) => ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0"][bar.index % 4],
    axisBottom: {
      tickRotation: -35,
      tickPadding: 15,
      legend: "District Names",
      legendOffset: 100,
      legendPosition: "middle",
    },
    axisLeft: { legend: "Custom Programme Submission Status", legendOffset: -50, legendPosition: "middle" },
  };

  const pieChartSettings = {
    margin: { top: 40, right: 20, bottom: 40, left: 20 },
    innerRadius: 0.5,
    padAngle: 0.7,
    cornerRadius: 4,
    activeOuterRadiusOffset: 6,
    colors: { scheme: "pastel1" },
  };

  const chartHeading = (title) => (
    <span>
      {title} ( District: <span style={{ color: "green", fontWeight: 600 }}>{appliedDistrictName}</span> |
      Activity: <span style={{ color: "green", fontWeight: 600 }}>{appliedActivityName}</span> |
      FY: <span style={{ color: "green", fontWeight: 600 }}>{appliedFYLabel}</span> )
    </span>
  );

  return (
    <div style={{ padding: 30, background: "#f4f6f8", minHeight: "100vh" }}>
      <h2 style={{ marginTop: 0 }}>Custom Programme Submission Status</h2>

      <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 20 }}>
        <label>
          District:
          <select value={selectedDistrict} onChange={(e) => setSelectedDistrict(e.target.value)} style={{ marginLeft: 10, padding: 5 }}>
            <option value="0">-- All --</option>
            {districts.map((d) => (
              <option key={d.dist_id} value={d.dist_id}>
                {d.district}
              </option>
            ))}
          </select>
        </label>

        <label>
          Activity:
          <select value={selectedActivity} onChange={(e) => setSelectedActivity(e.target.value)} style={{ marginLeft: 10, padding: 5 }}>
            <option value="0">-- All --</option>
            {activities.map((a) => (
              <option key={a.activities_id} value={a.activities_id}>
                {a.activity}
              </option>
            ))}
          </select>
        </label>

        <label>
          Financial Year:
          <select value={selectedFY} onChange={(e) => setSelectedFY(e.target.value)} style={{ marginLeft: 10, padding: 5 }}>
            {financialYears.map((fy) => (
              <option key={fy} value={fy}>
                {fy}
              </option>
            ))}
          </select>
        </label>

        <button
          style={{ padding: "10px 25px", background: "#2196F3", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", marginTop: -3 }}
          onClick={() => {
            setAppliedDistrict(selectedDistrict);
            setAppliedActivity(selectedActivity);
            setAppliedFY(selectedFY);
            fetchEnergyClub(selectedDistrict, selectedActivity, selectedFY);
          }}
        >
          Search
        </button>
      </div>

      <div style={{ height: 450, background: "#fff", padding: 20, borderRadius: 12, marginBottom: 35 }}>
        <h3>{chartHeading("Bar Chart: District-wise Custom Programme Submission Status")}</h3>

        <ResponsiveBar
          data={energyData}
          {...barChartSettings}
          tooltip={({ data }) => (
            <div style={{ padding: "6px 10px", background: "#fff", borderRadius: 6, boxShadow: "0 2px 6px rgba(0,0,0,0.2)" }}>
              <strong>{data.name}</strong>: {data.count}
            </div>
          )}
          isInteractive={true}
          onMouseEnter={() => (document.body.style.cursor = "pointer")}
          onMouseLeave={() => (document.body.style.cursor = "default")}
          onClick={(bar) => {
            // store filters and open view page
            localStorage.setItem("viewFromChart", "1");
            localStorage.setItem(
              "viewCustomFilters",
              JSON.stringify({
                district: String(bar.data.district_id),
                activity: String(appliedActivity || "0"),
                fy: appliedFY,
                status: "submitted",
              })
            );
            window.open("/view-custom-school", "_blank");
          }}
        />
      </div>

      <div style={{ height: 450, background: "#fff", padding: 20, borderRadius: 12 }}>
        <h3 style={{ marginBottom: 15 }}>{chartHeading("Pie Chart: District-wise Custom Programme Submission Status")}</h3>

        <ResponsivePie
          data={pieData}
          {...pieChartSettings}
          isInteractive={true}
          onMouseEnter={() => (document.body.style.cursor = "pointer")}
          onMouseLeave={() => (document.body.style.cursor = "default")}
          tooltip={({ datum }) => (
            <div style={{ padding: "6px 10px", background: "#fff", borderRadius: 6, boxShadow: "0 2px 6px rgba(0,0,0,0.2)" }}>
              <strong>{datum.label}</strong> : {datum.value}
            </div>
          )}
          arcLinkLabel={(d) => d.label}
          onClick={(slice) => {
            localStorage.setItem("viewFromChart", "1");
            localStorage.setItem(
              "viewCustomFilters",
              JSON.stringify({
                district: String(slice.data.district_id),
                activity: String(appliedActivity || "0"),
                fy: appliedFY,
                status: "submitted",
              })
            );
            window.open("/view-custom-school", "_blank");
          }}
        />
      </div>
    </div>
  );
}
